/*
================================================================================
File: src/pages/Training.js
Description: A simple component that links to the training website.
================================================================================
*/
import React from 'react';
import PageContent from '../components/common/PageContent';

const TopicCard = ({ icon, title, description, onClick }) => (
  <button
    onClick={onClick}
    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md dark:hover:shadow-lg transition-shadow text-left flex flex-col items-start gap-3 group h-full"
  >
    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-base">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
        {description}
      </p>
    </div>
  </button>
);

const Training = () => {
  const topics = [
    {
      icon: (
        <svg
          className="w-6 h-6 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6.253v11.494m-9-5.747h18"
          />
        </svg>
      ),
      title: "Getting Started",
      description: "Platform orientation and your first steps to success.",
      link: "https://training.bigdogtools.com/",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      ),
      title: "Skip Tracing",
      description: "Learn how to run skip traces and interpret results.",
      link: "https://training.bigdogtools.com/",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
      title: "AI Property Queries",
      description: "Use AI to analyze properties and get instant insights.",
      link: "https://training.bigdogtools.com/",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      ),
      title: "Managing Leads",
      description: "Organize and track seller and investor leads effectively.",
      link: "https://training.bigdogtools.com/",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      title: "Live Q&A Calls",
      description: "Join weekly group calls with the Big Dog team.",
      link: "https://training.bigdogtools.com/join-a-call/",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.125-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.125-1.283.356-1.857m0 0a3.001 3.001 0 015.288 0M12 14a4 4 0 100-8 4 4 0 000 8z"
          />
        </svg>
      ),
      title: "Referrals",
      description: "Learn how to register and track referrals.",
      link: "https://training.bigdogtools.com/referrals/",
    },
  ];

  return (
    <PageContent title="Training" error={null}>
      <div className="p-8 space-y-8">
        {/* Hero Section */}
        <div className="bg-green-600 rounded-xl p-8 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h1 className="text-3xl font-bold">Big Dog Training</h1>
          </div>
          <p className="text-green-100 text-lg mb-6 max-w-2xl">
            Master the platform with step-by-step guides, live Q&A calls, and
            in-depth tutorials. Everything you need to succeed is right here.
          </p>
          <button
            onClick={() =>
              window.open(
                "https://training.bigdogtools.com/",
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="px-6 py-3 bg-white text-green-700 font-bold rounded-lg hover:bg-green-50 transition-colors inline-flex items-center gap-2"
          >
            Access My Training
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Training Topics */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-6">
            Training Topics
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {topics.map((topic, idx) => (
              <TopicCard
                key={idx}
                icon={topic.icon}
                title={topic.title}
                description={topic.description}
                onClick={() =>
                  window.open(topic.link, "_blank", "noopener,noreferrer")
                }
              />
            ))}
          </div>
        </div>
      </div>
    </PageContent>
  );
};

export default Training;
